import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  DeleteOutline as DeleteOutlineIcon,
  OpenInNew as OpenInNewIcon,
  SmartToy as SmartToyIcon,
  Storage as StorageIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PartnerLayout from '../../components/partner/PartnerLayout';
import PartnerHelpPanel from '../../components/partner/PartnerHelpPanel';
import LoadingButton from '../../components/LoadingButton';
import AccountUserStatusChip from '../../components/AccountUserStatusChip';
import EmployeeStatusChip from '../../components/EmployeeStatusChip';
import CreditsSummary from '../../components/CreditsSummary';
import EmployeeCatalogLink from '../../components/EmployeeCatalogLink';
// Mismo selector de volúmenes (y mismo cálculo de impacto) que usan el portal
// del cliente y el panel admin: las reglas de tamaño son del backend.
import EmployeeSizePicker, {
  DEFAULT_EMPLOYEE_SIZE, EmployeeSize, EmployeeSizeChip, asSize
} from '../../components/EmployeeSizePicker';
import EmployeeStoragePicker, {
  EmployeeVolumes,
  defaultVolumes,
  storageImpact
} from '../../components/EmployeeStoragePicker';
import partnerApiService from '../../services/partnerApi';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';
import {
  AccountEmployee,
  CreditEntry,
  EmployeeUpdate,
  PublicListingOption
} from '../../types/clientAccount';
// El detalle que sirve el portal partner = el del admin + el almacenamiento de
// la CARTERA (lo que este partner reparte entre sus clientes).
import { PartnerClientDetail } from '../../types/partner';
import { formatCredits } from '../../utils/credits';
// Veredicto del tenant sobre el disco (applied/deferred/failed), leído igual
// aquí que en los portales de admin y cliente.
import { storageSyncFeedback } from '../../utils/storageSync';

// Detalle de cliente del PORTAL PARTNER: empleados con estado en vivo (sondeo
// mientras hay despliegues en curso), asignación por NOMBRE desde el catálogo
// público, asignación de créditos DESDE la bolsa mayorista (409 si no hay
// saldo) y «Ver como cliente» (impersonación solo-lectura en pestaña nueva).

const PartnerClientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const clientId = Number(id);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { reseller, refreshMe } = usePartnerAuth();

  const [account, setAccount] = useState<PartnerClientDetail | null>(null);
  // Aviso «hay versión superior en el marketplace» por empleado desplegado (best-effort).
  const [employeeUpdates, setEmployeeUpdates] = useState<EmployeeUpdate[]>([]);
  const [upgradingEmployeeId, setUpgradingEmployeeId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // REPARTO de almacenamiento: el partner fija lo incluido y el techo de ESTE
  // cliente. Puede hacerlo porque el disco de toda su cartera se lo factura el
  // marketplace a él (el cliente no paga almacenamiento), pero siempre dentro
  // de su propio techo agregado — el backend lo comprueba y devuelve 409 con
  // cuánto le queda, que es lo que se enseña en el diálogo.
  const [quotaOpen, setQuotaOpen] = useState(false);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [quotaError, setQuotaError] = useState('');
  const [quotaIncluded, setQuotaIncluded] = useState('20');
  const [quotaMax, setQuotaMax] = useState('0');

  // Asignar empleado — picker por NOMBRE (rellena el slug), como el admin.
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [listingOptions, setListingOptions] = useState<PublicListingOption[]>([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  // ADMINISTRADOR del empleado: usuario DEL CLIENTE (obligatorio). Es quien
  // administra el empleado y la identidad con la que este trabaja — ni el
  // partner ni el dueño de la cuenta valen para eso.
  const [adminUserId, setAdminUserId] = useState<number | ''>('');
  // Tamaño de los DOS volúmenes del empleado que se asigna. El partner lo elige
  // aquí porque el disco es suyo dos veces: consume el techo de la cuenta del
  // cliente Y su techo agregado de cartera, y ampliar después es permanente.
  const [assignVolumes, setAssignVolumes] = useState<EmployeeVolumes>({
    workspace_gib: 10, data_gib: 10
  });
  // CAPACIDAD (talla) del empleado. Nace marcada la que el partner tenga por
  // defecto: es su preferencia de cartera, no un tope — aquí puede cambiarla
  // para este empleado concreto.
  const [assignSize, setAssignSize] = useState<EmployeeSize>(DEFAULT_EMPLOYEE_SIZE);

  // Créditos desde la bolsa mayorista, en sus DOS formas (son dos productos,
  // no dos maneras de decir lo mismo):
  //   'topup' → RECARGA puntual: entra en la bolsa extra del cliente y NO
  //             caduca. Se paga una vez.
  //   'plan'  → PLAN MENSUAL: caduca cada ciclo en el cliente y cada renovación
  //             vuelve a salir de TU bolsa. Es recurrente y el diálogo lo dice.
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsError, setCreditsError] = useState('');
  const [creditsAmount, setCreditsAmount] = useState('');
  const [creditsConcept, setCreditsConcept] = useState('');
  const [creditsMode, setCreditsMode] = useState<'topup' | 'plan'>('topup');
  const [creditsCycleDay, setCreditsCycleDay] = useState('1');

  // «Ver como cliente» (impersonación).
  const [impersonating, setImpersonating] = useState(false);

  // Baja de un empleado del cliente (libera cupo) — con confirmación.
  const [removeTarget, setRemoveTarget] = useState<AccountEmployee | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  // Ampliar el disco de un empleado del cliente: irreversible y con cargo por
  // ciclo mientras el empleado exista, así que confirma en diálogo.
  const [expandTarget, setExpandTarget] = useState<AccountEmployee | null>(null);
  const [expandLoading, setExpandLoading] = useState(false);
  const [expandError, setExpandError] = useState('');

  const pollTimer = useRef<number | null>(null);

  const loadAccount = useCallback(async (silent: boolean = false) => {
    try {
      if (!silent) setError('');
      const data = await partnerApiService.getClient(clientId);
      setAccount(data);
      // Aviso de actualización: best-effort y aparte — un fallo no puede tumbar el detalle.
      try {
        setEmployeeUpdates(await partnerApiService.getClientEmployeeUpdates(clientId));
      } catch (updErr) {
        console.error('Failed to load employee updates:', updErr);
        setEmployeeUpdates([]);
      }
    } catch (err: any) {
      console.error('Failed to load partner client detail:', err);
      if (!silent) {
        setError(err?.detail || t('partner.clientDetail.loadError', 'No se pudo cargar el cliente'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [clientId, t]);

  useEffect(() => {
    if (!Number.isNaN(clientId)) loadAccount();
  }, [clientId, loadAccount]);

  // Lanza el upgrade de un empleado del cliente a la última versión y recarga al terminar.
  const handleUpgradeEmployee = useCallback(async (employeeId: number) => {
    setUpgradingEmployeeId(employeeId);
    try {
      await partnerApiService.upgradeClientEmployee(clientId, employeeId);
      await loadAccount(true);
    } catch (err: any) {
      setError(err?.detail || t('partner.clientDetail.upgradeError', 'No se pudo actualizar el empleado.'));
    } finally {
      setUpgradingEmployeeId(null);
    }
  }, [clientId, loadAccount, t]);

  // Estado EN VIVO: mientras haya despliegues en curso, refresco silencioso
  // cada 5 s para que el partner vea avanzar el estado sin pulsar nada.
  useEffect(() => {
    const hasInProgress = (account?.employees ?? []).some(
      (e) => e.status === 'pending' || e.status === 'provisioning'
    );
    if (hasInProgress && pollTimer.current === null) {
      pollTimer.current = window.setInterval(() => loadAccount(true), 5000);
    }
    if (!hasInProgress && pollTimer.current !== null) {
      window.clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    return () => {
      if (pollTimer.current !== null) {
        window.clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [account, loadAccount]);

  const usedSlots = account ? (account.employees ?? []).filter((e) => e.status !== 'removed').length : 0;
  const maxEmployees = typeof account?.max_employees === 'number' ? account.max_employees : null;
  const quotaFull = maxEmployees !== null && usedSlots >= maxEmployees;

  // Cupo AGREGADO del partner (empleados vivos de TODA su cartera), que llega
  // en /partner/me. 0 = sin límite. Se mira ANTES de abrir el diálogo para que
  // el tope se cuente aquí y no como un 409 después de rellenar el formulario.
  const globalCap = Number(reseller?.max_total_employees ?? 0);
  const globalUsed = Number(reseller?.employees_count ?? 0);
  const globalQuotaFull = globalCap > 0 && globalUsed >= globalCap;

  // Almacenamiento del cliente (lo calcula el backend sobre los volúmenes de
  // sus empleados vivos) y el tramo de ampliación de plataforma.
  const storage = account?.storage ?? null;
  const storageStep = storage?.step_gib ?? 20;

  // Impacto del tamaño elegido en el diálogo de asignar: en la CUENTA del
  // cliente (lo calcula el picker) y en el techo AGREGADO de la cartera del
  // partner, que es una guarda distinta y vive en /partner/me. Los dos se
  // comprueban aquí para decirlo antes, no como un 409 tras rellenar el
  // formulario — es el mismo orden que aplica el backend.
  const assignImpact = storageImpact(storage, assignVolumes);
  const storageCap = Number(reseller?.max_total_storage_gib ?? 0);
  const storageUsedPortfolio = Number(reseller?.storage_used_gib ?? 0);
  const overStorageCap = storageCap > 0
    && storageUsedPortfolio + assignImpact.total > storageCap;

  // Almacenamiento de la CARTERA (lo trae el propio detalle): cuánto reparte ya
  // y de cuánto dispone. Es el contexto sin el que fijar el techo de un cliente
  // sería a ciegas — «repartes 80 de 200 GiB».
  const portfolio = account?.portfolio_storage ?? null;

  const openQuotaDialog = () => {
    setQuotaIncluded(String(storage?.included_gib ?? 20));
    setQuotaMax(String(storage?.max_gib ?? 0));
    setQuotaError('');
    setQuotaOpen(true);
  };

  const quotaIncludedValid = /^\d+$/.test(quotaIncluded.trim()) && Number(quotaIncluded) >= 0;
  const quotaMaxValid = /^\d+$/.test(quotaMax.trim()) && Number(quotaMax) >= 0;
  const quotaValid = quotaIncludedValid && quotaMaxValid;

  const handleSaveQuota = async () => {
    if (!quotaValid) return;
    try {
      setQuotaLoading(true);
      setQuotaError('');
      await partnerApiService.updateClientStorage(clientId, {
        included_storage_gib: Number(quotaIncluded),
        max_storage_gib: Number(quotaMax)
      });
      setQuotaOpen(false);
      toast.success(t('partner.clientDetail.quotaSaved', 'Almacenamiento del cliente actualizado'));
      loadAccount();
      // Cambia lo repartido de su cartera → /partner/me lo recalcula.
      refreshMe();
    } catch (err: any) {
      // 409 = te pasas de tu techo agregado / cliente sin techo teniendo tú uno
      // / incluido por encima del techo. El detail dice cuánto le queda.
      console.error('Failed to update client storage:', err);
      setQuotaError(err?.detail
        || t('partner.clientDetail.quotaError', 'No se pudo actualizar el almacenamiento del cliente'));
    } finally {
      setQuotaLoading(false);
    }
  };

  const handleExpandStorage = async () => {
    if (!expandTarget) return;
    const ws = Number(expandTarget.workspace_gib ?? 10);
    const data = Number(expandTarget.data_gib ?? 10);
    try {
      setExpandLoading(true);
      setExpandError('');
      const res = await partnerApiService.expandEmployeeStorage(clientId, expandTarget.id, {
        workspace_gib: ws + Math.ceil(storageStep / 2),
        data_gib: data + Math.floor(storageStep / 2)
      });
      // El disco del cliente lo amplía el TENANT, no esta ficha: `sync` dice si
      // creció (applied), si queda pendiente de su próximo despliegue (deferred)
      // o si no se hizo nada (failed → nada apuntado, nada facturado, y el
      // diálogo se queda abierto con el motivo para reintentar).
      const feedback = storageSyncFeedback(res?.sync, t);
      if (feedback.failed) {
        setExpandError(feedback.message);
        loadAccount();
        return;
      }
      setExpandTarget(null);
      if (feedback.severity === 'warning') {
        toast.warning(feedback.message);
      } else {
        toast.success(feedback.message);
      }
      loadAccount();
    } catch (err: any) {
      // 409 con el motivo real: tramo, tope por volumen, techo de la cuenta o
      // TU techo agregado de cartera. Se queda dentro del diálogo.
      console.error('Failed to expand storage:', err);
      setExpandError(err.detail || t('partner.clientDetail.storageError', 'No se pudo ampliar el almacenamiento'));
    } finally {
      setExpandLoading(false);
    }
  };

  // Usuarios del cliente elegibles como administrador (los no deshabilitados;
  // uno pendiente sirve: su alta en la plataforma se reintenta al provisionar).
  const adminCandidates = (account?.users ?? []).filter((u) => u.status !== 'disabled');

  const openAssignDialog = async () => {
    setAssignOpen(true);
    setAssignError('');
    setSelectedSlug('');
    setDisplayName('');
    // Tamaño de salida = los mínimos del backend (lo que aplicaría sin mandar
    // nada): asignar sin tocar el selector cuesta lo de siempre.
    setAssignVolumes(defaultVolumes(storage));
    // Y la talla de salida = la que el partner tiene por defecto.
    setAssignSize(asSize(reseller?.default_employee_size));
    // Con un único usuario elegible se preselecciona: es la única opción.
    setAdminUserId(adminCandidates.length === 1 ? adminCandidates[0].id : '');
    try {
      setLoadingListings(true);
      const options = await partnerApiService.getPublicListingOptions();
      setListingOptions(options);
    } catch (err) {
      console.error('Failed to load listing catalog:', err);
      setAssignError(t('partner.clientDetail.catalogError', 'No se pudo cargar el catálogo de empleados'));
    } finally {
      setLoadingListings(false);
    }
  };

  const handleAssignEmployee = async () => {
    if (!selectedSlug || adminUserId === '') return;
    try {
      setAssignLoading(true);
      setAssignError('');
      await partnerApiService.assignEmployee(clientId, {
        listing_slug: selectedSlug,
        display_name: displayName.trim() || undefined,
        admin_account_user_id: adminUserId as number,
        // Los volúmenes viajan siempre: el empleado nace con el disco elegido
        // (el install del tenant los recibe de estas columnas), no hay un
        // segundo paso donde ampliarlos gratis.
        workspace_gib: assignVolumes.workspace_gib,
        data_gib: assignVolumes.data_gib,
        // CAPACIDAD: viaja igual que el disco — el empleado nace con ella.
        size: assignSize
      });
      toast.success(t('partner.clientDetail.employeeAssigned', 'Empleado asignado: el despliegue ha comenzado'));
      setAssignOpen(false);
      loadAccount();
      // La ocupación del cupo AGREGADO vive en /partner/me: sin refrescarlo, el
      // aviso de «al límite» llegaría un empleado tarde.
      refreshMe();
    } catch (err: any) {
      console.error('Failed to assign employee:', err);
      setAssignError(err?.detail || t('partner.clientDetail.assignError', 'No se pudo asignar el empleado'));
    } finally {
      setAssignLoading(false);
    }
  };

  // En modo PLAN se admite 0: es cómo se retira el plan (el ciclo vigente
  // termina su mes y no se abre otro). Una recarga de 0 no significa nada.
  const creditsNumber = Number(creditsAmount);
  const creditsValid =
    creditsAmount.trim() !== '' && !Number.isNaN(creditsNumber) &&
    (creditsMode === 'plan' ? creditsNumber >= 0 : creditsNumber > 0);

  // Lo que le CUESTA al partner cada asignación: créditos × su precio mayorista
  // plano (precio de lista × (1 − su descuento)). El crédito ya lo compró a
  // granel; esto le recuerda su coste antes de darle de más a un cliente. Lo
  // que le facture a su cliente es su decisión (su margen es su negocio).
  const wholesaleListPrice = Number(reseller?.credit_price_usd ?? 0);
  const wholesaleDiscount = Number(reseller?.wholesale_discount_pct ?? 0);
  const wholesaleUnit = wholesaleListPrice > 0
    ? wholesaleListPrice * (1 - wholesaleDiscount / 100)
    : null;
  const estAssignCost = wholesaleUnit !== null && creditsNumber > 0
    ? creditsNumber * wholesaleUnit
    : null;

  const handleAssignCredits = async () => {
    if (!creditsValid) return;
    try {
      setCreditsLoading(true);
      setCreditsError('');
      await partnerApiService.assignCredits(clientId, {
        amount: creditsNumber,
        concept: creditsConcept.trim() || undefined,
        mode: creditsMode,
        cycle_day: creditsMode === 'plan' ? Number(creditsCycleDay) || 1 : undefined
      });
      toast.success(
        creditsMode === 'plan'
          ? (creditsNumber > 0
              ? t('partner.clientDetail.planAssigned', 'Plan mensual activado: se renovará cada mes desde tu bolsa mayorista')
              : t('partner.clientDetail.planRemoved', 'Plan retirado: el ciclo en curso termina su mes y no se abrirá otro'))
          : t('partner.clientDetail.creditsAssigned', 'Créditos asignados desde tu bolsa mayorista'));
      setCreditsOpen(false);
      setCreditsAmount('');
      setCreditsConcept('');
      loadAccount();
      // El saldo mayorista del navbar/hero cambia: refrescar /partner/me.
      refreshMe();
    } catch (err: any) {
      console.error('Failed to assign credits:', err);
      // 409 = sin saldo mayorista suficiente → el detail del backend manda.
      setCreditsError(err?.detail || t('partner.clientDetail.creditsError', 'No se pudieron asignar los créditos (¿saldo mayorista insuficiente?)'));
    } finally {
      setCreditsLoading(false);
    }
  };

  // Abre el portal cliente en pestaña nueva con el token de impersonación
  // (solo lectura, 15 min). La ruta /customer/impersonate lo instala por-pestaña.
  const handleImpersonate = async () => {
    try {
      setImpersonating(true);
      const { access_token } = await partnerApiService.impersonateClient(clientId);
      window.open(
        `/customer/impersonate?token=${encodeURIComponent(access_token)}`,
        '_blank',
        'noopener'
      );
    } catch (err: any) {
      console.error('Failed to impersonate client:', err);
      toast.error(err?.detail || t('partner.clientDetail.impersonateError', 'No se pudo abrir la vista de cliente'));
    } finally {
      setImpersonating(false);
    }
  };

  // Retira el empleado del cliente: undeploy+borrado de su app en el tenant y
  // contrato REMOVED (libera cupo). `tenant_error` = la baja se completó pero
  // la app no pudo retirarse — se avisa sin bloquear.
  const handleRemoveEmployee = async () => {
    if (!removeTarget) return;
    try {
      setRemoveLoading(true);
      const res = await partnerApiService.removeEmployee(clientId, removeTarget.id);
      if (res?.tenant_error) {
        toast.warning(t(
          'partner.clientDetail.removeEmployeePartial',
          'Empleado retirado (cupo liberado), pero su aplicación no pudo eliminarse: {{error}}',
          { error: res.tenant_error }
        ));
      } else {
        toast.success(t('partner.clientDetail.employeeRemoved', 'Empleado eliminado'));
      }
      loadAccount(true);
      // Retirar libera hueco del cupo agregado: /partner/me lo recalcula.
      refreshMe();
    } catch (err: any) {
      console.error('Failed to remove employee:', err);
      toast.error(err?.detail || t('partner.clientDetail.removeEmployeeError', 'No se pudo eliminar el empleado'));
    } finally {
      setRemoveLoading(false);
      setRemoveTarget(null);
    }
  };

  const creditKindLabel = (entry: CreditEntry) => {
    switch (entry.kind) {
      case 'topup':
        return t('partner.clientDetail.creditKind.topup', 'Recarga');
      case 'adjustment':
        return t('partner.clientDetail.creditKind.adjustment', 'Ajuste');
      case 'consumption':
        return t('partner.clientDetail.creditKind.consumption', 'Consumo');
      default:
        return entry.kind;
    }
  };

  // Estado de la cuenta traducido; un estado nuevo del backend se enseña tal
  // cual en vez de quedarse en blanco.
  const statusLabel = (status: string) =>
    status === 'active'
      ? t('partner.clients.statusActive', 'Activa')
      : status === 'suspended'
        ? t('partner.clients.statusSuspended', 'Suspendida')
        : status;

  const employeeName = (employeeId: number | null): string => {
    if (employeeId === null || !account) return '—';
    const emp = (account.employees ?? []).find((e) => e.id === employeeId);
    return emp ? (emp.display_name || emp.listing_slug) : `#${employeeId}`;
  };

  // ── Usuarios del cliente (dar de alta + asociar a empleados) ──
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserAuth, setNewUserAuth] = useState<'password' | 'password_google' | 'google'>('password');
  const [addUserError, setAddUserError] = useState('');

  const [assignUserOpen, setAssignUserOpen] = useState(false);
  const [assignUserId, setAssignUserId] = useState<number | null>(null);
  const [assignEmpId, setAssignEmpId] = useState<number | ''>('');
  const [assignRole, setAssignRole] = useState<'member' | 'admin'>('member');
  const [assignUserLoading, setAssignUserLoading] = useState(false);
  const [assignUserError, setAssignUserError] = useState('');

  const liveEmployees = (account?.employees ?? []).filter((e) => e.status !== 'removed');

  const handleAddUser = async () => {
    try {
      setAddUserLoading(true); setAddUserError('');
      const res = await partnerApiService.addClientUser(clientId, {
        email: newUserEmail.trim(), full_name: newUserName.trim(), auth_method: newUserAuth,
      });
      setAddUserOpen(false);
      setNewUserEmail(''); setNewUserName('');
      toast.success(res.generated_password
        ? t('partner.clientDetail.userCreatedPw', 'Usuario creado. Contraseña temporal: {{pw}}', { pw: res.generated_password })
        : t('partner.clientDetail.userCreated', 'Usuario creado'));
      await loadAccount(true);
    } catch (err: any) {
      setAddUserError(err?.detail || t('partner.clientDetail.userCreateError', 'No se pudo crear el usuario'));
    } finally { setAddUserLoading(false); }
  };

  const openAssignUser = (userId: number) => {
    setAssignUserId(userId); setAssignEmpId(''); setAssignRole('member'); setAssignUserError(''); setAssignUserOpen(true);
  };

  const handleAssignUser = async () => {
    if (assignUserId === null || assignEmpId === '') return;
    try {
      setAssignUserLoading(true); setAssignUserError('');
      await partnerApiService.assignClientUser(clientId, assignUserId, {
        account_employee_id: Number(assignEmpId), role: assignRole,
      });
      setAssignUserOpen(false);
      toast.success(t('partner.clientDetail.userAssigned', 'Usuario asociado al empleado'));
      await loadAccount(true);
    } catch (err: any) {
      setAssignUserError(err?.detail || t('partner.clientDetail.userAssignError', 'No se pudo asociar el usuario'));
    } finally { setAssignUserLoading(false); }
  };

  const handleUnassignUser = async (userId: number, empId: number) => {
    try {
      await partnerApiService.unassignClientUser(clientId, userId, empId);
      toast.success(t('partner.clientDetail.userUnassigned', 'Usuario quitado del empleado'));
      await loadAccount(true);
    } catch (err: any) {
      toast.error(err?.detail || t('partner.clientDetail.userUnassignError', 'No se pudo quitar'));
    }
  };

  if (isLoading) {
    return (
      <PartnerLayout>
        <Box display="flex" justifyContent="center" py={10}>
          <CircularProgress />
        </Box>
      </PartnerLayout>
    );
  }

  if (error || !account) {
    return (
      <PartnerLayout>
        <Container maxWidth="lg" sx={{ py: 5 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/partner/clients')} sx={{ mb: 2 }}>
            {t('common.back', 'Atrás')}
          </Button>
          <Alert severity="error">
            {error || t('partner.clientDetail.notFound', 'Cliente no encontrado')}
          </Alert>
        </Container>
      </PartnerLayout>
    );
  }

  return (
    <PartnerLayout>
      <Container maxWidth="lg" sx={{ py: 5 }}>
        {/* Cabecera */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/partner/clients')}>
              {t('common.back', 'Atrás')}
            </Button>
            <Typography variant="h4">{account.name}</Typography>
            {/* Ayuda de ESTA pantalla: plan vs recarga, cupos, disco, «ver
                como cliente» y qué es irreversible. */}
            <PartnerHelpPanel screen="clientDetail" variant="icon" />
            <Chip
              label={statusLabel(account.status)}
              color={account.status === 'active' ? 'success' : 'default'}
              size="small"
            />
          </Box>
          <Box display="flex" gap={1.5} flexWrap="wrap">
            <LoadingButton
              variant="outlined"
              startIcon={<VisibilityIcon />}
              loading={impersonating}
              onClick={handleImpersonate}
            >
              {t('partner.clientDetail.impersonate', 'Ver como cliente')}
            </LoadingButton>
            <Button variant="contained" startIcon={<WalletIcon />} onClick={() => { setCreditsOpen(true); setCreditsError(''); }}>
              {t('partner.clientDetail.assignCredits', 'Asignar créditos')}
            </Button>
          </Box>
        </Box>

        {/* Datos + créditos */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('partner.clientDetail.accountData', 'Datos del cliente')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('partner.clientDetail.fields.owner', 'Propietario')}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {account.owner_email || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('partner.clientDetail.fields.company', 'Empresa')}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {account.company || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('partner.clientDetail.fields.createdAt', 'Creada')}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {format(new Date(account.created_at), 'dd/MM/yyyy HH:mm')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('partner.clientDetail.fields.quota', 'Cupo de empleados')}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {maxEmployees !== null ? `${usedSlots} / ${maxEmployees}` : usedSlots}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <WalletIcon color="primary" />
                  <Typography variant="h6">{t('partner.clientDetail.credits', 'Créditos del cliente')}</Typography>
                </Box>
                <CreditsSummary data={account} balance={account.balance} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ALMACENAMIENTO del cliente y reparto de la cartera. Aquí no se habla
            de coste PARA EL CLIENTE (no paga disco: lo paga el partner por el
            total de su cartera), sino de cuánto le está dando de SU capacidad
            — que es la decisión que se toma en esta pantalla. */}
        {storage && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <StorageIcon color="primary" />
                  <Typography variant="h6">
                    {t('partner.clientDetail.storageTitle', 'Almacenamiento de este cliente')}
                  </Typography>
                </Box>
                <Button variant="outlined" startIcon={<StorageIcon />} onClick={openQuotaDialog}>
                  {t('partner.clientDetail.editQuota', 'Ajustar almacenamiento')}
                </Button>
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partner.storage.used', 'En uso')}
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {storage.max_gib > 0
                      ? t('partner.dashboard.metricStorageOfCap', '{{used}} de {{max}} GiB',
                          { used: storage.used_gib, max: storage.max_gib })
                      : t('partner.dashboard.metricStorageValue', '{{used}} GiB', { used: storage.used_gib })}
                  </Typography>
                  {storage.max_gib === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {t('partner.clientDetail.storageNoCap', 'Sin techo propio')}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partner.clientDetail.storageIncluded', 'Incluido para el cliente')}
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {t('partner.dashboard.metricStorageValue', '{{used}} GiB', { used: storage.included_gib })}
                  </Typography>
                </Grid>
                {portfolio && (
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      {t('partner.clientDetail.portfolioStorage', 'Tu cartera')}
                    </Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {portfolio.max_gib > 0
                        ? t('partner.storage.allocatedShort', '{{allocated}} de {{max}} GiB repartidos',
                            { allocated: portfolio.allocated_gib, max: portfolio.max_gib })
                        : t('partner.dashboard.metricStorageValue', '{{used}} GiB', { used: portfolio.used_gib })}
                    </Typography>
                    {Number(portfolio.billable_gib) > 0 && (
                      <Typography variant="caption" color="warning.main">
                        {t('partner.storage.billableCaption',
                          '{{gib}} GiB facturables · {{credits}} créditos/ciclo',
                          {
                            gib: portfolio.billable_gib,
                            credits: formatCredits(portfolio.cycle_credits)
                          })}
                      </Typography>
                    )}
                  </Grid>
                )}
              </Grid>
              <Typography variant="body2" color="text.secondary" mt={2}>
                {t(
                  'partner.clientDetail.storageExplainer',
                  'Este cliente no paga almacenamiento: el disco de toda tu cartera te lo factura el marketplace a ti. Aquí decides cuánto le incluyes y hasta dónde puede crecer.'
                )}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Empleados IA — estado en vivo */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <SmartToyIcon color="primary" />
                <Typography variant="h6">
                  {t('partner.clientDetail.employees', 'Empleados IA')}{' '}
                  ({maxEmployees !== null ? `${usedSlots}/${maxEmployees}` : (account.employees ?? []).length})
                </Typography>
              </Box>
              <Tooltip
                title={quotaFull
                  ? t('partner.clientDetail.quotaFullHint', 'Cupo completo: contacta con el proveedor para ampliarlo')
                  : globalQuotaFull
                    ? t('partner.clientDetail.globalQuotaFullHint',
                        'Has alcanzado tu límite de empleados IA en toda tu cartera ({{used}}/{{max}})',
                        { used: globalUsed, max: globalCap })
                    : ''}
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openAssignDialog}
                    disabled={quotaFull || globalQuotaFull}
                  >
                    {t('partner.clientDetail.assignEmployee', 'Asignar empleado')}
                  </Button>
                </span>
              </Tooltip>
            </Box>
            {/* Aviso de actualización: empleados desplegados con versión superior en el
                marketplace. El upgrade se ofrecerá aparte (increment siguiente). */}
            {employeeUpdates.some((u) => u.update_available) && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>
                  {t('partner.clientDetail.updatesTitle', 'Hay nuevas versiones disponibles en el marketplace')}
                </AlertTitle>
                {employeeUpdates
                  .filter((u) => u.update_available)
                  .map((u) => (
                    <Box key={u.employee_id} sx={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <span>
                        <strong>{u.display_name || u.listing_slug}</strong>:{' '}
                        {t('partner.clientDetail.updateFromTo', 'de {{from}} a {{to}}', {
                          from: u.installed_version || '—',
                          to: u.latest_version || '—'
                        })}
                      </span>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={upgradingEmployeeId === u.employee_id}
                        onClick={() => handleUpgradeEmployee(u.employee_id)}
                        sx={{ textTransform: 'none', ml: 'auto' }}
                      >
                        {upgradingEmployeeId === u.employee_id
                          ? t('partner.clientDetail.upgrading', 'Actualizando…')
                          : t('partner.clientDetail.upgrade', 'Actualizar')}
                      </Button>
                    </Box>
                  ))}
              </Alert>
            )}
            {(account.employees ?? []).length === 0 ? (
              <Typography color="text.secondary" align="center" py={3}>
                {t('partner.clientDetail.noEmployees', 'Este cliente todavía no tiene empleados IA. Asígnale el primero desde el catálogo.')}
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('partner.clientDetail.fields.employee', 'Empleado')}</TableCell>
                      <TableCell>{t('partner.clientDetail.fields.admin', 'Administrador')}</TableCell>
                      <TableCell>{t('partner.clientDetail.fields.status', 'Estado')}</TableCell>
                      <TableCell>{t('partner.clientDetail.fields.app', 'Aplicación')}</TableCell>
                      <TableCell align="right">{t('partner.clientDetail.fields.allocatedCredits', 'Créditos asignados')}</TableCell>
                      <TableCell align="right">{t('partner.clientDetail.fields.storage', 'Disco')}</TableCell>
                      <TableCell>{t('partner.clientDetail.fields.createdAt', 'Creada')}</TableCell>
                      <TableCell align="right">{t('partner.clientDetail.fields.actions', 'Acciones')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(account.employees ?? []).map((emp) => (
                      <TableRow key={emp.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {emp.display_name || emp.listing_slug}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {emp.listing_slug}
                            {emp.listing_version ? ` · v${emp.listing_version}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {/* Empleados anteriores al modelo de administrador:
                              se dice, no se deja el hueco en blanco. */}
                          {emp.admin_user_name ? (
                            <Tooltip title={emp.admin_user_email || ''}>
                              <Typography variant="body2">{emp.admin_user_name}</Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t('partner.clientDetail.noAdminDeclared', 'Sin administrador declarado')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <EmployeeStatusChip status={emp.status} />
                            {emp.status === 'failed' && emp.error_message && (
                              <Tooltip title={emp.error_message}>
                                <Typography
                                  variant="caption"
                                  color="error"
                                  sx={{
                                    maxWidth: 220,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'inline-block'
                                  }}
                                >
                                  {emp.error_message}
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {emp.status === 'active' && emp.app_url ? (
                            <Link
                              href={emp.app_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                            >
                              {t('partner.clientDetail.openApp', 'Abrir app')}
                              <OpenInNewIcon fontSize="inherit" />
                            </Link>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {emp.allocated_credits === null || emp.allocated_credits === undefined ? (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          ) : (
                            <Typography variant="body2" fontWeight={600}>
                              {formatCredits(emp.allocated_credits)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {/* Los dos volúmenes del empleado: lo que ocupa de
                              tu techo agregado de almacenamiento. */}
                          <Tooltip
                            title={t('partner.clientDetail.storageBreakdown', '{{ws}} GiB espacio de trabajo + {{data}} GiB datos', {
                              ws: emp.workspace_gib ?? 10,
                              data: emp.data_gib ?? 10
                            })}
                          >
                            <Typography variant="body2" fontWeight={600}>
                              {emp.storage_gib ?? Number(emp.workspace_gib ?? 10) + Number(emp.data_gib ?? 10)} GiB
                            </Typography>
                          </Tooltip>
                          {/* CAPACIDAD con la que está desplegado. Va aquí, en
                              la misma celda, porque es la otra mitad de «con
                              qué recursos trabaja» — y porque cambiarla, como
                              el disco, exige volver a desplegarlo. */}
                          <Box sx={{ mt: 0.5 }}>
                            <EmployeeSizeChip size={emp.size} />
                          </Box>
                        </TableCell>
                        <TableCell>{format(new Date(emp.created_at), 'dd/MM/yyyy')}</TableCell>
                        <TableCell align="right">
                          <Box display="flex" gap={1} justifyContent="flex-end">
                            {/* Ampliar disco: permanente y con cargo por ciclo
                                mientras el empleado exista. */}
                            {emp.status !== 'removed' && (
                              <Button
                                size="small"
                                startIcon={<StorageIcon />}
                                onClick={() => {
                                  setExpandTarget(emp);
                                  setExpandError('');
                                }}
                              >
                                {t('partner.clientDetail.expandStorage', 'Ampliar disco')}
                              </Button>
                            )}
                            {/* Baja del empleado: retira su app del tenant y
                                LIBERA una plaza del cupo del cliente. */}
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteOutlineIcon />}
                              onClick={() => setRemoveTarget(emp)}
                            >
                              {t('partner.clientDetail.removeEmployee', 'Eliminar')}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Usuarios del cliente — necesarios para levantar y administrar empleados */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
              <Typography variant="h6">{t('partner.clientDetail.users', 'Usuarios del cliente')}</Typography>
              <Button variant="outlined" onClick={() => { setNewUserEmail(''); setNewUserName(''); setNewUserAuth('password'); setAddUserError(''); setAddUserOpen(true); }}>
                {t('partner.clientDetail.addUser', 'Añadir usuario')}
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t('partner.clientDetail.usersHint', 'Un empleado necesita un usuario del cliente como administrador. Da de alta usuarios aquí y asócialos a los empleados.')}
            </Typography>
            {(account.users ?? []).length === 0 ? (
              <Alert severity="info">
                {t('partner.clientDetail.noUsers', 'Aún no hay usuarios. Añade al menos uno para poder levantar empleados.')}
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('partner.clientDetail.fields.userEmail', 'Email')}</TableCell>
                      <TableCell>{t('partner.clientDetail.fields.userName', 'Nombre')}</TableCell>
                      <TableCell>{t('partner.clientDetail.fields.userStatus', 'Estado')}</TableCell>
                      <TableCell>{t('partner.clientDetail.fields.assignedEmployees', 'Empleados asignados')}</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(account.users ?? []).map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.full_name || '—'}</TableCell>
                        <TableCell><AccountUserStatusChip user={user} /></TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {(user.assignments ?? []).length === 0 ? (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            ) : (user.assignments ?? []).map((a) => (
                              <Chip key={a.account_employee_id} size="small"
                                label={`${a.employee_name}${a.role === 'admin' ? ' · admin' : ''}`}
                                onDelete={() => handleUnassignUser(user.id, a.account_employee_id)} />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => openAssignUser(user.id)} disabled={liveEmployees.length === 0}>
                            {t('partner.clientDetail.assignToEmployee', 'Asignar a empleado')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Diálogo: añadir usuario a la cuenta del cliente */}
        <Dialog open={addUserOpen} onClose={() => setAddUserOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('partner.clientDetail.addUser', 'Añadir usuario')}</DialogTitle>
          <DialogContent>
            {addUserError && <Alert severity="error" sx={{ mb: 2 }}>{addUserError}</Alert>}
            <TextField autoFocus margin="dense" type="email" fullWidth sx={{ mb: 2 }}
              label={t('partner.clientDetail.fields.userEmail', 'Email')}
              value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} />
            <TextField margin="dense" fullWidth sx={{ mb: 2 }}
              label={t('partner.clientDetail.fields.userName', 'Nombre')}
              value={newUserName} onChange={(e) => setNewUserName(e.target.value)} />
            <FormControl fullWidth>
              <InputLabel>{t('partner.clientDetail.fields.authMethod', 'Método de acceso')}</InputLabel>
              <Select value={newUserAuth} label={t('partner.clientDetail.fields.authMethod', 'Método de acceso')}
                onChange={(e) => setNewUserAuth(e.target.value as 'password' | 'password_google' | 'google')}>
                <MenuItem value="password">{t('partner.clientDetail.auth.password', 'Contraseña')}</MenuItem>
                <MenuItem value="password_google">{t('partner.clientDetail.auth.passwordGoogle', 'Contraseña y Google')}</MenuItem>
                <MenuItem value="google">{t('partner.clientDetail.auth.google', 'Solo Google')}</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddUserOpen(false)}>{t('common.cancel', 'Cancelar')}</Button>
            <LoadingButton variant="contained" loading={addUserLoading}
              disabled={!newUserEmail.trim() || !newUserName.trim()} onClick={handleAddUser}>
              {t('partner.clientDetail.addUser', 'Añadir usuario')}
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Diálogo: asociar un usuario a un empleado */}
        <Dialog open={assignUserOpen} onClose={() => setAssignUserOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('partner.clientDetail.assignToEmployee', 'Asignar a empleado')}</DialogTitle>
          <DialogContent>
            {assignUserError && <Alert severity="error" sx={{ mb: 2 }}>{assignUserError}</Alert>}
            <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
              <InputLabel>{t('partner.clientDetail.fields.employee', 'Empleado')}</InputLabel>
              <Select value={assignEmpId} label={t('partner.clientDetail.fields.employee', 'Empleado')}
                onChange={(e) => setAssignEmpId(e.target.value as number)}>
                {liveEmployees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>{emp.display_name || emp.listing_slug}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('partner.clientDetail.fields.role', 'Rol')}</InputLabel>
              <Select value={assignRole} label={t('partner.clientDetail.fields.role', 'Rol')}
                onChange={(e) => setAssignRole(e.target.value as 'member' | 'admin')}>
                <MenuItem value="member">{t('partner.clientDetail.role.member', 'Miembro')}</MenuItem>
                <MenuItem value="admin">{t('partner.clientDetail.role.admin', 'Administrador')}</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignUserOpen(false)}>{t('common.cancel', 'Cancelar')}</Button>
            <LoadingButton variant="contained" loading={assignUserLoading} disabled={assignEmpId === ''} onClick={handleAssignUser}>
              {t('partner.clientDetail.assign', 'Asignar')}
            </LoadingButton>
          </DialogActions>
        </Dialog>

        {/* Movimientos de créditos */}
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <WalletIcon color="primary" />
              <Typography variant="h6">{t('partner.clientDetail.creditLedger', 'Movimientos de créditos')}</Typography>
            </Box>
            {(account.credit_entries ?? []).length === 0 ? (
              <Typography color="text.secondary" align="center" py={3}>
                {t('partner.clientDetail.noCreditEntries', 'Sin movimientos todavía.')}
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common.date', 'Fecha')}</TableCell>
                      <TableCell>{t('partner.clientDetail.fields.kind', 'Tipo')}</TableCell>
                      <TableCell>{t('partner.clientDetail.fields.concept', 'Concepto')}</TableCell>
                      <TableCell>{t('partner.clientDetail.fields.employee', 'Empleado')}</TableCell>
                      <TableCell align="right">{t('partner.clientDetail.fields.credits', 'Créditos')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(account.credit_entries ?? []).map((entry) => {
                      const amount = Number(entry.amount);
                      return (
                        <TableRow key={entry.id} hover>
                          <TableCell>{format(new Date(entry.created_at), 'dd/MM/yyyy HH:mm')}</TableCell>
                          <TableCell>
                            <Chip
                              label={creditKindLabel(entry)}
                              size="small"
                              color={entry.kind === 'topup' ? 'success' : entry.kind === 'consumption' ? 'default' : 'info'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{entry.concept || '—'}</TableCell>
                          <TableCell>{employeeName(entry.account_employee_id)}</TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={amount < 0 ? 'error.main' : 'success.main'}
                            >
                              {amount > 0 ? '+' : ''}{formatCredits(entry.amount)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Container>

      {/* Diálogo asignar empleado — picker por NOMBRE del catálogo público */}
      <Dialog open={assignOpen} onClose={() => !assignLoading && setAssignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('partner.clientDetail.assignEmployee', 'Asignar empleado')}</DialogTitle>
        <DialogContent>
          {assignError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAssignError('')}>
              {assignError}
            </Alert>
          )}
          {/* Sin usuarios del cliente no se puede asignar: el empleado necesita
              un administrador real, que es su identidad de trabajo. El partner
              no da de alta usuarios del cliente (su acceso «ver como cliente» es
              de solo lectura), así que el aviso apunta a quien puede hacerlo. */}
          {adminCandidates.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
              {t('partner.clientDetail.noAdminCandidates',
                'Este cliente todavía no tiene usuarios. Pídele que añada al menos uno desde su portal («Usuarios»): ese usuario será el administrador del empleado y la identidad con la que trabaje.')}
            </Alert>
          )}
          {/* Cupo AGREGADO lleno: se dice AQUÍ, antes de rellenar nada, en vez
              de dejar que el backend responda 409 al pulsar «Asignar». */}
          {globalQuotaFull && (
            <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
              {t('partner.clientDetail.globalQuotaFull',
                'Has alcanzado tu límite de empleados IA ({{used}}/{{max}} sumando todos tus clientes). Retira alguno que ya no se use o pide una ampliación al marketplace: cada empleado es una aplicación desplegada y el límite gobierna esa carga.',
                { used: globalUsed, max: globalCap })}
            </Alert>
          )}
          {/* El selector solo da nombres: el catálogo público es donde se ve
              qué hace cada empleado. Va JUNTO al picker, antes de elegir. */}
          <EmployeeCatalogLink sx={{ mt: 1, mb: 1 }} />
          <FormControl fullWidth variant="outlined" margin="dense" sx={{ mb: 2, mt: 1 }}>
            <InputLabel>{t('partner.clientDetail.fields.listingPicker', 'Empleado del catálogo')} *</InputLabel>
            <Select
              value={selectedSlug}
              onChange={(e) => setSelectedSlug(e.target.value as string)}
              label={t('partner.clientDetail.fields.listingPicker', 'Empleado del catálogo') + ' *'}
              disabled={loadingListings}
            >
              <MenuItem value="" disabled>
                {loadingListings
                  ? t('common.loading', 'Cargando...')
                  : t('partner.clientDetail.selectListing', 'Selecciona un empleado')}
              </MenuItem>
              {listingOptions.map((option) => (
                <MenuItem key={option.slug} value={option.slug}>
                  {option.display_name || option.slug}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Administrador del empleado — selector por NOMBRE (rellena el id) */}
          <FormControl fullWidth variant="outlined" margin="dense" sx={{ mb: 2 }}>
            <InputLabel>{t('partner.clientDetail.fields.employeeAdmin', 'Administrador del empleado')} *</InputLabel>
            <Select
              value={adminUserId === '' ? '' : String(adminUserId)}
              onChange={(e) => setAdminUserId(Number(e.target.value))}
              label={t('partner.clientDetail.fields.employeeAdmin', 'Administrador del empleado') + ' *'}
              disabled={adminCandidates.length === 0}
            >
              <MenuItem value="" disabled>
                {t('partner.clientDetail.selectAdmin', 'Selecciona un usuario del cliente')}
              </MenuItem>
              {adminCandidates.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>
                  {u.full_name} · {u.email}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {t('partner.clientDetail.employeeAdminHelp',
                'Administrará el empleado y será la identidad con la que este trabaje en la plataforma.')}
            </FormHelperText>
          </FormControl>
          <TextField
            margin="dense"
            label={t('partner.clientDetail.fields.displayName', 'Nombre para mostrar') + ' (' + t('common.optional', 'Opcional') + ')'}
            fullWidth
            variant="outlined"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            helperText={t('partner.clientDetail.displayNameHelp', 'Si lo dejas vacío se usará el nombre del catálogo')}
            sx={{ mb: 2 }}
          />
          {/* ALMACENAMIENTO del empleado: se elige al asignarlo, con lo que
              consume del incluido del cliente y lo que le costará por ciclo. */}
          <EmployeeStoragePicker
            storage={storage}
            value={assignVolumes}
            onChange={setAssignVolumes}
            disabled={assignLoading}
          />
          {/* CAPACIDAD del empleado. Viene marcada la del partner y se dice de
              dónde sale: si no, parecería una elección que él no hizo. */}
          <EmployeeSizePicker
            value={assignSize}
            onChange={setAssignSize}
            disabled={assignLoading}
            defaultFrom="partner"
          />
          {/* Techo AGREGADO de disco de la cartera: guarda del PARTNER (no de la
              cuenta), así que se dice aparte y con sus propias cifras — el
              backend la comprueba ANTES que las de la cuenta. */}
          {overStorageCap && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {t('partner.clientDetail.storageCapFull',
                'Tu límite de almacenamiento de cartera es {{max}} GiB y ya usas {{used}}: con este empleado llegarías a {{after}}. Elige un tamaño menor, retira algún empleado o pide una ampliación al marketplace.',
                {
                  max: storageCap,
                  used: storageUsedPortfolio,
                  after: storageUsedPortfolio + assignImpact.total
                })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)} disabled={assignLoading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton
            onClick={handleAssignEmployee}
            loading={assignLoading}
            disabled={!selectedSlug || adminUserId === '' || globalQuotaFull
              // Los dos techos de disco (cuenta del cliente y cartera del
              // partner) acabarían en 409: se frena aquí con el motivo visible.
              || assignImpact.overCap || overStorageCap}
            variant="contained"
          >
            {t('partner.clientDetail.assign', 'Asignar')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Créditos al cliente — sale de la bolsa mayorista del partner, en sus
          DOS formas. La elección va ARRIBA del importe a propósito: cambia lo
          que significa la cifra (una vez vs todos los meses) y el partner tiene
          que decidirlo antes de escribirla, no después. */}
      <Dialog open={creditsOpen} onClose={() => !creditsLoading && setCreditsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('partner.clientDetail.assignCredits', 'Asignar créditos')}</DialogTitle>
        <DialogContent>
          {creditsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreditsError('')}>
              {creditsError}
            </Alert>
          )}
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={creditsMode}
            onChange={(_e, value) => value && setCreditsMode(value)}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="topup" sx={{ textTransform: 'none' }}>
              {t('partner.clientDetail.modeTopup', 'Recarga puntual')}
            </ToggleButton>
            <ToggleButton value="plan" sx={{ textTransform: 'none' }}>
              {t('partner.clientDetail.modePlan', 'Plan mensual')}
            </ToggleButton>
          </ToggleButtonGroup>
          {/* Qué implica cada modo, sin letra pequeña: el plan es RECURRENTE y
              sus créditos CADUCAN en el cliente; la recarga se paga una vez y
              no caduca. */}
          <Alert severity={creditsMode === 'plan' ? 'warning' : 'info'} sx={{ mb: estAssignCost !== null ? 1 : 2 }}>
            {creditsMode === 'plan'
              ? t('partner.clientDetail.modePlanHint', 'Recurrente: cada renovación volverá a descontar este importe de tu bolsa mayorista, y los créditos del plan caducan en tu cliente al cerrar cada ciclo. Tu saldo actual: {{balance}} créditos.', {
                  balance: formatCredits(reseller?.wholesale_balance)
                })
              : t('partner.clientDetail.wholesaleHint', 'Recarga puntual: entra en la bolsa extra del cliente, NO caduca y se paga una vez. Se descuenta de tu bolsa mayorista (saldo actual: {{balance}}).', {
                  balance: formatCredits(reseller?.wholesale_balance)
                })}
          </Alert>
          {/* Lo que le CUESTA a él: créditos × su precio mayorista. Su margen
              (lo que le cobre a su cliente) es su decisión, no se calcula aquí. */}
          {estAssignCost !== null && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {creditsMode === 'plan'
                ? t('partner.clientDetail.planCostHint', 'Cada mes te cuesta ~{{cost}} USD ({{credits}} créditos × tu precio mayorista). Lo que le factures a tu cliente lo decides tú.', {
                    cost: estAssignCost.toFixed(2),
                    credits: formatCredits(creditsNumber)
                  })
                : t('partner.clientDetail.topupCostHint', 'Te cuesta ~{{cost}} USD ({{credits}} créditos × tu precio mayorista). Lo que le cobres a tu cliente lo decides tú.', {
                    cost: estAssignCost.toFixed(2),
                    credits: formatCredits(creditsNumber)
                  })}
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label={(creditsMode === 'plan'
              ? t('partner.clientDetail.fields.monthlyCredits', 'Créditos al mes')
              : t('partner.clientDetail.fields.credits', 'Créditos')) + ' *'}
            type="number"
            fullWidth
            variant="outlined"
            value={creditsAmount}
            onChange={(e) => setCreditsAmount(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            error={creditsAmount !== '' && !creditsValid}
            helperText={creditsAmount !== '' && !creditsValid
              ? (creditsMode === 'plan'
                  ? t('partner.clientDetail.amountNotNegative', 'El plan no puede ser negativo (0 = retirar el plan)')
                  : t('partner.clientDetail.amountPositive', 'El importe debe ser mayor que cero'))
              : (creditsMode === 'plan'
                  ? t('partner.clientDetail.planZeroHint', '0 retira el plan: el ciclo en curso termina su mes y no se abre otro.')
                  : undefined)}
            sx={{ mb: 2 }}
          />
          {creditsMode === 'plan' && (
            <TextField
              margin="dense"
              label={t('partner.clientDetail.fields.cycleDay', 'Día de renovación')}
              type="number"
              fullWidth
              variant="outlined"
              value={creditsCycleDay}
              onChange={(e) => setCreditsCycleDay(e.target.value)}
              inputProps={{ min: 1, max: 28, step: 1 }}
              helperText={t('partner.clientDetail.cycleDayHelp', 'Del 1 al 28, para que exista en todos los meses.')}
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            margin="dense"
            label={t('partner.clientDetail.fields.concept', 'Concepto') + ' (' + t('common.optional', 'Opcional') + ')'}
            fullWidth
            variant="outlined"
            value={creditsConcept}
            onChange={(e) => setCreditsConcept(e.target.value)}
            disabled={creditsMode === 'plan'}
            helperText={creditsMode === 'plan'
              ? t('partner.clientDetail.conceptPlanHelp', 'En el plan el concepto lo pone el sistema en cada renovación.')
              : undefined}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreditsOpen(false)} disabled={creditsLoading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton onClick={handleAssignCredits} loading={creditsLoading} disabled={!creditsValid} variant="contained">
            {creditsMode === 'plan'
              ? t('partner.clientDetail.activatePlan', 'Activar plan')
              : t('partner.clientDetail.assign', 'Asignar')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* REPARTIR almacenamiento a este cliente: lo incluido (lo que su portal
          le enseña como incluido en su servicio) y su techo. Sale de TU
          capacidad, así que el diálogo dice de cuánto dispones. */}
      <Dialog open={quotaOpen} onClose={() => !quotaLoading && setQuotaOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('partner.clientDetail.editQuota', 'Ajustar almacenamiento')}</DialogTitle>
        <DialogContent>
          {quotaError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setQuotaError('')}>
              {quotaError}
            </Alert>
          )}
          {portfolio && portfolio.max_gib > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('partner.storage.allocatedHint',
                'Repartes {{allocated}} de {{max}} GiB de tu cartera. Lo que des a este cliente sale de ahí.',
                { allocated: portfolio.allocated_gib, max: portfolio.max_gib })}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label={t('partner.clientDetail.fields.includedStorage', 'Incluido para el cliente (GiB)')}
            type="number"
            fullWidth
            variant="outlined"
            value={quotaIncluded}
            onChange={(e) => setQuotaIncluded(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            error={!quotaIncludedValid}
            helperText={!quotaIncludedValid
              ? t('partner.clientDetail.gibInvalid', 'Debe ser un entero mayor o igual que 0')
              : t('partner.clientDetail.includedStorageHelp',
                  'Lo que su portal le enseña como incluido en su servicio. No le genera coste: el disco lo pagas tú')}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('partner.clientDetail.fields.maxStorage', 'Techo del cliente (GiB)')}
            type="number"
            fullWidth
            variant="outlined"
            value={quotaMax}
            onChange={(e) => setQuotaMax(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            error={!quotaMaxValid}
            helperText={!quotaMaxValid
              ? t('partner.clientDetail.gibInvalid', 'Debe ser un entero mayor o igual que 0')
              : t('partner.clientDetail.maxStorageHelp',
                  'Hasta dónde puede crecer este cliente. 0 = sin techo propio (solo si tu cartera tampoco lo tiene). Bajarlo no encoge ningún disco ya creado: frena ampliaciones futuras')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuotaOpen(false)} disabled={quotaLoading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton
            onClick={handleSaveQuota}
            loading={quotaLoading}
            disabled={!quotaValid}
            variant="contained"
          >
            {t('common.save', 'Guardar')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* AMPLIAR el disco de un empleado — permanente y con cargo por ciclo */}
      <Dialog
        open={expandTarget !== null}
        onClose={() => !expandLoading && setExpandTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('partner.clientDetail.expandStorageTitle', 'Ampliar almacenamiento del empleado')}</DialogTitle>
        <DialogContent>
          {expandError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExpandError('')}>
              {expandError}
            </Alert>
          )}
          {expandTarget && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('partner.clientDetail.fields.employee', 'Empleado')}:{' '}
                <strong>{expandTarget.display_name || expandTarget.listing_slug}</strong>
                {' · '}
                {t('partner.clientDetail.storageFromTo', '{{from}} → {{to}} GiB', {
                  from: Number(expandTarget.workspace_gib ?? 10) + Number(expandTarget.data_gib ?? 10),
                  to: Number(expandTarget.workspace_gib ?? 10) + Number(expandTarget.data_gib ?? 10) + storageStep
                })}
              </Typography>
              {storage && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('partner.clientDetail.storageUsage', 'Este cliente usa {{used}} de {{included}} GiB incluidos', {
                    used: storage.used_gib,
                    included: storage.included_gib
                  })}
                </Typography>
              )}
              <Alert severity="warning">
                <AlertTitle>{t('partner.clientDetail.storageWarningTitle', 'Es permanente')}</AlertTitle>
                <Typography variant="body2">
                  {t('partner.clientDetail.storageWarningPermanent',
                    'El disco no se puede reducir después: encogerlo obligaría a recrear el volumen perdiendo los datos. Solo retirar el empleado libera el espacio, y el tamaño nuevo se aplica al reiniciar su aplicación.')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {/* El cargo es TUYO, no del cliente: el almacenamiento se
                      cobra una sola vez y a quien tiene la relación comercial. */}
                  {t('partner.clientDetail.storageWarningCost',
                    'Estos GiB se suman a tu cartera: cuentan para tu límite de almacenamiento y, si superan lo que tienes incluido, se te cobran a TI por ciclo. El cliente no ve ningún coste de almacenamiento.')}
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpandTarget(null)} disabled={expandLoading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton
            onClick={handleExpandStorage}
            loading={expandLoading}
            variant="contained"
            color="warning"
          >
            {t('partner.clientDetail.expandStorageConfirm', 'Ampliar +{{step}} GiB', { step: storageStep })}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Confirmación de baja de empleado — DELETE /partner/clients/{id}/employees/{empId} */}
      <Dialog
        open={removeTarget !== null}
        onClose={() => !removeLoading && setRemoveTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('partner.clientDetail.removeEmployeeTitle', 'Eliminar empleado')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {t(
              'partner.clientDetail.removeEmployeeConfirm',
              '¿Eliminar a «{{employee}}» de este cliente? Se retirará su aplicación y sus datos, y sus usuarios dejarán de tener acceso.',
              { employee: removeTarget?.display_name || removeTarget?.listing_slug || '' }
            )}
          </Typography>
          <Alert severity="warning" sx={{ mt: 1 }}>
            {t(
              'partner.clientDetail.removeEmployeeQuotaNote',
              'Se liberará una plaza del cupo del cliente, que podrá volver a usarse. La acción no se puede deshacer.'
            )}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)} disabled={removeLoading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton
            onClick={handleRemoveEmployee}
            loading={removeLoading}
            variant="contained"
            color="error"
          >
            {t('partner.clientDetail.removeEmployee', 'Eliminar')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </PartnerLayout>
  );
};

export default PartnerClientDetailPage;
